import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, useModuleAccess } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredPermission?: {
    resource: string;
    action: string;
  };
  requiredModule?: string;
  fallback?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  requiredPermission,
  requiredModule,
  fallback = '/unauthorized'
}) => {
  const { isAuthenticated, role, isLoading } = useAuth();
  const { hasModuleAccess } = useModuleAccess();
  const location = useLocation();
  
  // Pokazuj loader podczas sprawdzania sesji
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Sprawdzanie uprawnień...</p>
        </div>
      </div>
    );
  }
  
  // Jeśli nie zalogowany, przekieruj do logowania
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Sprawdź dostęp do modułu jeśli wymagany
  if (requiredModule) {
    if (!hasModuleAccess(requiredModule)) {
      console.log(`❌ User doesn't have access to module: ${requiredModule}`);
      return <Navigate to={fallback} replace />;
    }
  }
  
  // Sprawdź role jeśli wymagane
  if (requiredRoles.length > 0 && role) {
    const hasRequiredRole = requiredRoles.includes(role.name);
    if (!hasRequiredRole && role.name !== 'admin') { // Admin ma zawsze dostęp
      return <Navigate to={fallback} replace />;
    }
  }
  
  // TODO: Sprawdź konkretne uprawnienia gdy będą zaimplementowane
  if (requiredPermission) {
    // Na razie pomijamy, bo wymaga to integracji z usePermission
    // const { hasPermission } = usePermission();
    // if (!hasPermission(requiredPermission.resource, requiredPermission.action)) {
    //   return <Navigate to={fallback} replace />;
    // }
  }
  
  return <>{children}</>;
};

// Export dla kompatybilności wstecznej
export default ProtectedRoute;
